import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefCallback,
} from 'react'

export type DraggablePosition = { left: number; top: number }

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

type DraggableHandlers = {
  ref: RefCallback<HTMLElement>
  style: CSSProperties | undefined
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
}

/**
 * Makes a fixed toast/chip drag-movable so it can be moved off covered UI.
 * Use `didDrag()` to suppress click after a drag on chip/buttons.
 */
export function useDraggableToast() {
  const [position, setPosition] = useState<DraggablePosition | null>(null)
  const elRef = useRef<HTMLElement | null>(null)
  const draggingRef = useRef(false)
  const movedRef = useRef(false)
  const startRef = useRef({ pointerX: 0, pointerY: 0, left: 0, top: 0 })

  const setNodeRef = useCallback<RefCallback<HTMLElement>>((node) => {
    elRef.current = node
  }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('[data-no-drag]')) return

    const el = elRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    draggingRef.current = true
    movedRef.current = false
    startRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: rect.left,
      top: rect.top,
    }
    el.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!draggingRef.current) return

    const dx = event.clientX - startRef.current.pointerX
    const dy = event.clientY - startRef.current.pointerY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      movedRef.current = true
    }
    if (!movedRef.current) return

    const el = elRef.current
    const width = el?.offsetWidth ?? 0
    const height = el?.offsetHeight ?? 0
    const pad = 8
    const left = clamp(
      startRef.current.left + dx,
      pad,
      Math.max(pad, window.innerWidth - width - pad),
    )
    const top = clamp(
      startRef.current.top + dy,
      pad,
      Math.max(pad, window.innerHeight - height - pad),
    )
    setPosition({ left, top })
  }, [])

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    try {
      elRef.current?.releasePointerCapture(event.pointerId)
    } catch {
      /* already released */
    }
  }, [])

  const didDrag = useCallback(() => movedRef.current, [])

  const style: CSSProperties | undefined = position
    ? {
        left: position.left,
        top: position.top,
        right: 'auto',
        bottom: 'auto',
        transform: 'none',
      }
    : undefined

  const dragHandlers: DraggableHandlers = {
    ref: setNodeRef,
    style,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  }

  return { dragHandlers, didDrag, isPositioned: position != null }
}
